/**
 * Foundation module for booking-page embed mode.
 *
 * Detects when a booking page is loaded inside an iframe with `?embed=1`,
 * sends ready/resize/success postMessage events to the host window, and
 * preserves the query params that matter across step navigation.
 */
import { useEffect, useRef } from 'react';

export const EMBED_PARAM = 'embed';
// NOTE: these three string literals are duplicated (not imported) in static/js/ahoy-booking.js's
// wireMessageListener() switch, because that file must stay a dependency-free static asset with
// no build step. If you change a value here, update it there too — see the drift-guard test in
// frontend/src/test/ahoy-embed/ahoy-booking.test.ts (protocol contract section).
export const BOOKING_READY = 'ahoy:booking:ready';
export const BOOKING_RESIZE = 'ahoy:booking:resize';
export const BOOKING_SUCCESS = 'ahoy:booking:success';

// Fallback poll cadence for clients without ResizeObserver — frequent enough to
// track step transitions (calendar → form → success) without being busy.
export const RESIZE_POLL_INTERVAL_MS = 250;

export function isEmbedRequested(p: URLSearchParams): boolean {
  return p.get(EMBED_PARAM) === '1';
}

export function isFramed(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

export function isEmbedActive(p: URLSearchParams): boolean {
  return isEmbedRequested(p) && isFramed();
}

/**
 * Senders are send-only: target origin is '*' because the receiving widget
 * (not this page) is responsible for guarding the message origin.
 */
export function postBookingReady(): void {
  if (!isFramed()) {
    return;
  }
  window.parent.postMessage({ type: BOOKING_READY }, '*');
}

export function postBookingResize(height: number): void {
  if (!isFramed()) {
    return;
  }
  window.parent.postMessage({ type: BOOKING_RESIZE, height }, '*');
}

export function postBookingSuccess(payload: {
  bookingId: string;
  meetingLink: string;
}): void {
  if (!isFramed()) {
    return;
  }
  window.parent.postMessage(
    {
      type: BOOKING_SUCCESS,
      bookingId: payload.bookingId,
      meetingLink: payload.meetingLink,
    },
    '*'
  );
}

/**
 * Carries forward only the params that must survive step navigation
 * (embed mode, prefill, and UTM tracking) — everything else is dropped.
 */
export function preserveBookingParams(
  prev: URLSearchParams,
  step?: 'form' | 'success'
): URLSearchParams {
  const next = new URLSearchParams();
  for (const [key, value] of prev) {
    if (
      key === 'embed' ||
      key === 'name' ||
      key === 'email' ||
      key.startsWith('utm_')
    ) {
      next.set(key, value);
    }
  }
  if (step) {
    next.set('step', step);
  }
  return next;
}

export function useBookingEmbedMessaging(embed: boolean) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!embed) {
      return;
    }
    postBookingReady();
    // Track the last height we posted so repeat measurements — the observer's
    // first callback (which fires synchronously on observe() with the same
    // initial size), or an unchanged poll tick — don't emit a duplicate
    // ahoy:booking:resize.
    let lastSentHeight = 0;
    const measure = () => {
      const height = cardRef.current?.offsetHeight ?? 0;
      if (height > 0 && height !== lastSentHeight) {
        lastSentHeight = height;
        postBookingResize(height);
      }
    };
    measure();
    const el = cardRef.current;
    if (el && typeof window.ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    // No ResizeObserver: poll the card height so content transitions still
    // resize the host iframe. measure() de-dupes, so unchanged ticks post
    // nothing and only genuine height changes reach the host.
    const intervalId = window.setInterval(measure, RESIZE_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [embed]);
  return cardRef;
}
